"use client";

import clsx from "clsx";
import { motion } from "framer-motion";
import React, { useEffect, useRef, useState } from "react";

type MaxWidth =
  | "sm"
  | "md"
  | "lg"
  | "xl"
  | "2xl"
  | "3xl"
  | "4xl"
  | "5xl"
  | "6xl"
  | "7xl"
  | "full";

interface HeroTitleProps {
  badge?: string;
  title: string;
  description?: string | string[];
  align?: "left" | "center" | "right";
  titleMaxWidth?: MaxWidth;
  descriptionMaxWidth?: MaxWidth;
}

const maxWidthMap: Record<MaxWidth, string> = {
  sm: "max-w-sm",
  md: "max-w-md",
  lg: "max-w-lg",
  xl: "max-w-xl",
  "2xl": "max-w-2xl",
  "3xl": "max-w-3xl",
  "4xl": "max-w-4xl",
  "5xl": "max-w-5xl",
  "6xl": "max-w-6xl",
  "7xl": "max-w-7xl",
  full: "max-w-full",
};

const renderWithLineBreaks = (text?: string) => {
  if (!text) return null;

  return text.split("\n").map((line, index, arr) => (
    <React.Fragment key={index}>
      {line}
      {index < arr.length - 1 && <br />}
    </React.Fragment>
  ));
};

const HeroTitle: React.FC<HeroTitleProps> = ({
  badge,
  title,
  description,
  align = "center",
  titleMaxWidth = "5xl",
  descriptionMaxWidth = "3xl",
}) => {
  const [isVisible, setIsVisible] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!ref.current) return;

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) setIsVisible(true);
      },
      {
        threshold: 0.1,
        rootMargin: "0px 0px -50px 0px",
      }
    );

    observer.observe(ref.current);

    return () => observer.disconnect();
  }, []);

  const alignmentClasses = clsx(
    "flex flex-col",
    align === "left" && "items-start lg:text-left text-center",
    align === "center" && "items-center text-center",
    align === "right" && "items-end text-right"
  );

  return (
    <motion.div
      ref={ref}
      initial={{ opacity: 0, y: 40 }}
      animate={isVisible ? { opacity: 1, y: 0 } : {}}

      className={alignmentClasses}
    >
      {/* Pill Label */}
      {badge && (
        <div className="mb-4 mx-auto lg:mx-0">
          <div className="inline-flex items-center rounded border -skew-x-16  bg-[#D53333] px-4 py-1.5 text-xs lg:text-sm font-medium text-[#fff]">
            <span className="skew-x-16   "> {badge}</span>
          </div>
        </div>
      )}

      {/* Main Heading */}
      <h1
        className={clsx(
          "text-3xl !font-bold tracking-tight text-[#272d57]",
         
          "2xl:text-5xl",

          "leading-[1.10]",
          maxWidthMap[titleMaxWidth]
        )}
      >
        {renderWithLineBreaks(title)}
      </h1>

      {/* Description */}
      {description && (
        <div className="mt-2 flex flex-col gap-4">
          {(Array.isArray(description)
            ? description
            : [description]
          ).map((desc, index) => (
            <p
              key={index}
              className={clsx(
                "!text-sm  2xl:text-lg ",
                "leading-snug text-[#4b5563]",
                maxWidthMap[descriptionMaxWidth]
              )}
            >
              {renderWithLineBreaks(desc)}
            </p>
          ))}
        </div>
      )}
    </motion.div>
  );
};

export default HeroTitle;